/**
 * Spears ("Mounts of Mayhem" drop): seven tiers, a long-reach JAB and a speed-scaled CHARGE.
 * Pure data + functions (DOM-free) so main.ts, mobs and smoke/spear.ts share one source of truth.
 *
 * PRIMARY SOURCE: Mojang/bedrock-samples behavior_pack/items/<tier>_spear.json (damage, cooldown = swing_duration, durability,
 * piercing_weapon reach 2..4.5 + hitbox_margin 0.25, kinetic_weapon delay / damage_multiplier / stage max_durations and min speeds,
 * use_modifiers.movement_modifier 1.0). Lunge is not in the packs (engine-side): wiki 0.458 b/tick per level.
 * Older notes: Bedrock jab damage + cooldowns, kinetic_weapon stage times / dismount speeds / delays (minecraft.wiki
 * Spear page, cumulative seconds x 20 ticks), Lunge 0.458 b/tick per level (minecraft.wiki Lunge page).
 * Anything left "unverified" is a tuned guess: mob spear weights and the Bedrock stab swing length.
 */

import { I } from './itemIds';
import type { Stack } from './types';
import { E } from './enchant/registry';
import { enchLevel } from './enchant/stack';

export const SPEAR_KEYS = ['WOODEN', 'STONE', 'COPPER', 'IRON', 'GOLDEN', 'DIAMOND', 'NETHERITE'] as const;
export type SpearKey = typeof SPEAR_KEYS[number];

export interface SpearTier {
  key: SpearKey;
  /** Item id. */
  id: number;
  /** Jab damage (half-hearts). */
  damage: number;
  /** Jab attacks per second: the cooldown is 20 / speed ticks. */
  speed: number;
  /** Charge damage multiplier on the relative speed. */
  chargeMult: number;
  /** Durability (the tool tier's). */
  dur: number;
  /** Mining-tier number kept for ToolDef.tier (spears do not mine). */
  tier: number;
  /** Charge stage ends in ticks since the charge began: dismount/engaged, knockback/tired, damage/total (est.). */
  stages: readonly [number, number, number];
  /** Ticks after use starts before the charge can hurt anything (kinetic_weapon delay). */
  delay: number;
  /** Relative speed (blocks/s) needed to unseat a rider while engaged. */
  dismountBps: number;
  /** Crafting / anvil repair material. */
  repair: number;
}

const T = (key: SpearKey, damage: number, speed: number, chargeMult: number, dur: number, tier: number,
  stages: readonly [number, number, number], delay: number, dismountBps: number, repair: number): SpearTier =>
  ({ key, id: (I as Record<string, number>)[`${key}_SPEAR`], damage, speed, chargeMult, dur, tier, stages, delay, dismountBps, repair });

export const SPEAR_TIERS: readonly SpearTier[] = [
  T('WOODEN', 1, 1 / 0.65, 0.7, 60, 0, [100, 200, 300], 15, 14, 0),   // repair: any planks (resolved in recipes/anvil)
  T('STONE', 2, 1 / 0.75, 0.82, 130, 1, [90, 180, 275], 14, 13, 0),   // repair: cobblestone family
  T('COPPER', 2, 1 / 0.85, 0.82, 190, 1, [80, 165, 250], 13, 12, I.COPPER_INGOT),
  T('IRON', 3, 1 / 0.95, 0.95, 250, 2, [50, 135, 225], 12, 11, I.IRON_INGOT),
  T('GOLDEN', 1, 1 / 0.95, 0.7, 30, 0, [70, 170, 275], 14, 13, I.GOLD_INGOT),
  T('DIAMOND', 4, 1 / 1.05, 1.075, 1560, 3, [60, 130, 200], 10, 10, I.DIAMOND),
  T('NETHERITE', 5, 1 / 1.15, 1.2, 2030, 4, [50, 110, 175], 8, 9, I.NETHERITE_INGOT),
];
const BY_ID = new Map<number, SpearTier>(SPEAR_TIERS.map((t) => [t.id, t]));

export const spearTier = (id: number): SpearTier | undefined => BY_ID.get(id);
export const isSpearId = (id: number): boolean => BY_ID.has(id);
export const isSpear = (s: Stack | null | undefined): boolean => !!s && s.count > 0 && BY_ID.has(s.id);

// --- jab ---------------------------------------------------------------------------------

/** Hitbox margin (blocks) added around targets for the jab ray (piercing_weapon.hitbox_margin). */
export const JAB_HITBOX_MARGIN = 0.25;
/** Jab reach window in blocks, measured eye -> the target's hitbox surface: nothing nearer than MIN can be hit. */
export const JAB_MIN_REACH = 2;
export const JAB_MAX_REACH = 4.5;
export const jabInReach = (dist: number): boolean => dist >= JAB_MIN_REACH && dist <= JAB_MAX_REACH;
/** Ticks between jabs (Bedrock spears keep a real cooldown, unlike other melee). */
export const jabCooldownTicks = (id: number): number => { const t = BY_ID.get(id); return t ? 20 / t.speed : 0; };
export const jabReady = (id: number, ticksSinceJab: number): boolean => ticksSinceJab >= jabCooldownTicks(id);

export interface Boxed { x: number; y: number; z: number; width: number; height: number; alive?: boolean }

/** Entry distance of the ray eye + t*dir (dir normalised) into the mob's box, or -1. */
export function rayBoxDist(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
  b: Boxed, maxDist: number, grow = 0): number {
  const hw = b.width / 2 + grow;
  const lo = [b.x - hw, b.y - grow, b.z - hw], hi = [b.x + hw, b.y + b.height + grow, b.z + hw];
  const o = [ox, oy, oz], d = [dx, dy, dz];
  let t0 = 0, t1 = maxDist;
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]) < 1e-9) { if (o[i] < lo[i] || o[i] > hi[i]) return -1; continue; }
    let a = (lo[i] - o[i]) / d[i], c = (hi[i] - o[i]) / d[i];
    if (a > c) { const s = a; a = c; c = s; }
    t0 = Math.max(t0, a); t1 = Math.min(t1, c);
    if (t0 > t1) return -1;
  }
  return t0;
}

/**
 * Every mob a jab pierces along the look line inside the reach window, nearest first. `blockDist` is the
 * distance to the first solid block on the ray (Infinity if none): mobs behind it are shielded.
 */
export function jabTargets<M extends Boxed>(mobs: readonly M[], ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number, blockDist = Infinity): { mob: M; dist: number }[] {
  const l = Math.hypot(dx, dy, dz);
  if (l < 1e-9) return [];
  const ux = dx / l, uy = dy / l, uz = dz / l;
  const out: { mob: M; dist: number }[] = [];
  for (const m of mobs) {
    if (m.alive === false) continue;
    const t = rayBoxDist(ox, oy, oz, ux, uy, uz, m, JAB_MAX_REACH, JAB_HITBOX_MARGIN);
    if (t < 0 || !jabInReach(t) || t > blockDist) continue;
    out.push({ mob: m, dist: t });
  }
  return out.sort((a, b) => a.dist - b.dist);
}

// --- charge ------------------------------------------------------------------------------

export type ChargeStage = 'engaged' | 'tired' | 'disengaged' | 'ended';
/** The charge's tip reach in blocks (eye -> tip). */
export const CHARGE_REACH = 3;
/** Minimum closing speed (blocks/s): below it no damage, below KB no knockback. */
export const CHARGE_MIN_DMG_BPS = 4.6;
export const CHARGE_MIN_KB_BPS = 5.1;
/** Stage 0..2 for visuals (steady, tired, dropped); 2 after the end. */
export const chargeStageIndex = (s: ChargeStage): 0 | 1 | 2 => (s === 'engaged' ? 0 : s === 'tired' ? 1 : 2);
/** Ground speed multiplier while a charge is held on foot. */
export const CHARGE_WALK_MULT = 1; // use_modifiers.movement_modifier 1.0: no slowdown, so speed can be built up

/** Stage after `ticks` ticks of charging: engaged -> tired -> disengaged -> ended. */
export function chargeStage(id: number, ticks: number): ChargeStage {
  const t = BY_ID.get(id);
  if (!t) return 'ended';
  if (ticks < t.stages[0]) return 'engaged';
  if (ticks < t.stages[1]) return 'tired';
  if (ticks < t.stages[2]) return 'disengaged';
  return 'ended';
}
/** Knockback scale by stage (engaged full, tired half, disengaged none). Base strength stays the KNOCKBACK workstream's 0.4. */
export const chargeKnockbackScale = (s: ChargeStage): number => (s === 'engaged' ? 1 : s === 'tired' ? 0.5 : 0);
/** Only the engaged stage unseats riders. */
export const chargeDismounts = (s: ChargeStage): boolean => s === 'engaged';

/** Closing speed (blocks / second) of attacker onto target along the unit look direction; velocities in blocks / tick. */
export function relativeSpeedBps(av: readonly [number, number, number], tv: readonly [number, number, number],
  dir: readonly [number, number, number]): number {
  const l = Math.hypot(dir[0], dir[1], dir[2]) || 1;
  const rel = ((av[0] - tv[0]) * dir[0] + (av[1] - tv[1]) * dir[1] + (av[2] - tv[2]) * dir[2]) / l;
  return Math.max(0, rel * 20);
}

/** Charge hit damage (before Sharpness etc.) in half-hearts: closing speed (blocks/s) x tier multiplier (est.). */
export function chargeDamage(id: number, relSpeedBps: number): number {
  const t = BY_ID.get(id);
  return t && relSpeedBps >= CHARGE_MIN_DMG_BPS ? relSpeedBps * t.chargeMult : 0;
}
/** A charge hit under this much damage does nothing (standing still / backing away). */
export const CHARGE_MIN_DAMAGE = 0.5;

// --- Lunge -------------------------------------------------------------------------------

export const lungeLevel = (s: Stack | null | undefined): number => (s && isSpear(s) ? enchLevel(s, E.LUNGE) : 0);
/** Horizontal launch speed in blocks / tick per level (wiki: 0.458 x level). */
export const LUNGE_IMPULSE = [0, 0.458, 0.916, 1.374] as const;
/** Hunger points needed to lunge at all. */
export const LUNGE_MIN_FOOD = 6;
/** Exhaustion per level (4 = one hunger point, est.) and extra durability per level (est.). */
export const lungeExhaustion = (lvl: number): number => 4 * lvl;
export const lungeExtraDurability = (lvl: number): number => (lvl > 0 ? 1 : 0);

export type LungeBlock = '' | 'starving' | 'water' | 'gliding';
export function lungeBlocked(c: { food: number; creative?: boolean; inWater: boolean; gliding: boolean }): LungeBlock {
  if (c.gliding) return 'gliding';
  if (c.inWater) return 'water';
  if (!c.creative && c.food < LUNGE_MIN_FOOD) return 'starving';
  return '';
}
/** Horizontal velocity added by a lunge along the look direction (pitch ignored: purely horizontal). */
export function lungeVelocity(lvl: number, yaw: number): [number, number] {
  const s = LUNGE_IMPULSE[Math.max(0, Math.min(3, lvl))];
  return [-Math.sin(yaw) * s, -Math.cos(yaw) * s];
}

// --- mobs --------------------------------------------------------------------------------

/** Chance a zombie / husk / zombified piglin / piglin spawns holding a spear (vanilla drop chance 8.5%; spawn-with chance unverified). */
export const MOB_SPEAR_CHANCE = 0.05;
const MOB_TIER_WEIGHTS: Record<string, readonly [SpearKey, number][]> = {
  zombie: [['IRON', 1]],
  husk: [['IRON', 1]],
  zombified_piglin: [['GOLDEN', 1]],
  piglin: [['GOLDEN', 1]],
};
/** Spear item a freshly spawned mob of `type` carries, or 0. */
export function rollMobSpear(type: string, rnd: () => number): number {
  const w = MOB_TIER_WEIGHTS[type];
  if (!w || rnd() >= MOB_SPEAR_CHANCE) return 0;
  let r = rnd();
  for (const [k, p] of w) { r -= p; if (r <= 0) return (I as Record<string, number>)[`${k}_SPEAR`]; }
  return (I as Record<string, number>)[`${w[0][0]}_SPEAR`];
}
/** A spear-holding mob's charge hit: closing speed (blocks / tick) x tier multiplier, at least the jab damage. */
export function mobSpearDamage(id: number, closingBpt: number): number {
  const t = BY_ID.get(id);
  return t ? Math.max(t.damage, chargeDamage(id, Math.max(0, closingBpt * 20))) : 0;
}
